import * as React from "react";
import * as styles from "./PeopleSearch.css";
import "../../styles/BasicIA.css";
import UserService from "../../services/UserService";
import { IUserResult } from "../../interfaces/IUserResult";
import { IAPersonaSmall } from '../personaSmall/PersonaSmall';
import { IAIcon } from "../icon/Icon";
import { Helper } from "../../Helper";
import { IUser } from "../../interfaces/IUser";
import { LocalizationService } from "../../services/LocalizationService";
import { ENTER_KEY, SPACEBAR_KEY } from "../../global/Constants";

export interface IIAPeopleSearchProps {
  environment: "Development" | "Test" | "Production";
  tenant: string;
  token: string;
  multiSelection: boolean;
  selectedValues: IUser[];
  style?: React.CSSProperties;
  onSelect: (people: IUser[]) => void;
  showGroups?: boolean;
  backgroundColor?: string;
  placeholderText?: string;
  hightlightColor?: string;
  disabed?: boolean;
}

export interface IIAPeopleSearchState {
  userResult: IUserResult;
  isLoading: boolean;
  selectedPeople: IUser[];
  searchQuery: string;
}

export class IAPeopleSearch extends React.Component<IIAPeopleSearchProps, IIAPeopleSearchState> {

  private localizationService: LocalizationService = new LocalizationService();

  constructor(props: IIAPeopleSearchProps, state: IIAPeopleSearchState) {
    super(props, state);
    this.state = {
      userResult: undefined,
      isLoading: false,
      selectedPeople: props.selectedValues != undefined ? props.selectedValues : [],
      searchQuery: ""
    };
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  public componentWillReceiveProps(props: IIAPeopleSearchProps) {
    if (props.selectedValues != undefined) {
      this.setState({ selectedPeople: props.selectedValues });
    }
  }

  public render(): React.ReactElement<IIAPeopleSearchProps> {
    let suggestedPeople: JSX.Element[] = [];
    let selectedPeople: JSX.Element[] = [];
    if (this.state.selectedPeople?.length > 0) {
      this.state.selectedPeople.forEach(person => {
        selectedPeople.push(
          <div
            className={styles.selectedPeopleItemContainer}
            key={`selectedPeopleItemContainer_${person?.displayName}`}
          >
            <div className={styles.selectedPeopleItem}>
              <IAPersonaSmall
                name={person?.displayName}
                profileImage={Helper.getProfileOrGroupImage(this.props.environment, this.props.tenant, this.props.token, person?.email, person?.loginName || person?.userPrincipalName)}
                styles={{ width: "auto", marginTop: "0px", minWidth: "110px" }}
                backgroundColor={this.props.hightlightColor}
              />
              <div
                className={[styles.removePerson, "IA_OnFocus"].join(" ")}
                tabIndex={0}
                role="button"
                aria-label="Remove"
                onClick={() => {
                  // Remove person
                  let selectedPeople = this.state.selectedPeople;
                  let index = selectedPeople.indexOf(person);
                  if (index != -1) {
                    selectedPeople.splice(index, 1);
                  }
                  this.setState({ selectedPeople });
                  this.props.onSelect(selectedPeople);
                }}
                onKeyDown={(event) => {
                  if (event.key === ENTER_KEY || event.key === SPACEBAR_KEY) {
                    event.preventDefault();
                    // Remove person
                    let selectedPeople = this.state.selectedPeople;
                    let index = selectedPeople.indexOf(person);
                    if (index != -1) {
                      selectedPeople.splice(index, 1);
                    }
                    this.setState({ selectedPeople });
                    this.props.onSelect(selectedPeople);
                  }
                }}
              >
                <IAIcon
                  title="Add"
                  size={18}
                  color="#333333"
                  style={{
                    transform: "rotate(45deg)",
                    marginTop: 2,
                    marginLeft: -2
                  }}
                />
              </div>
            </div>
          </div>
        );
      });
    }
    if (this.state.userResult != undefined && this.state.userResult.users != undefined) {
      this.state.userResult.users.forEach(user => {
        suggestedPeople.push(
          <div
            className={[styles.suggestedPeopleItem, "IA_OnFocus"].join(" ")}
            key={`suggestedPeopleItem_${user.userPrincipalName}`}
            tabIndex={0}
            role="button"
            onClick={() => {
              // Add person
              let selectedPeople = this.state.selectedPeople;
              selectedPeople.push(user);
              this.setState({ selectedPeople, searchQuery: "", userResult: undefined });
              let inputField = document.getElementById("peopleSearchField");
              if (inputField) {
                inputField.focus();
              }
              this.props.onSelect(selectedPeople);
            }}
            onKeyDown={(event) => {
              if (event.key === ENTER_KEY || event.key === SPACEBAR_KEY) {
                event.preventDefault();
                // Add person
                let selectedPeople = this.state.selectedPeople;
                selectedPeople.push(user);
                this.setState({ selectedPeople, searchQuery: "", userResult: undefined });
                let inputField = document.getElementById("peopleSearchField");
                if (inputField) {
                  inputField.focus();
                }
                this.props.onSelect(selectedPeople);
              }
            }}
          >
            <IAPersonaSmall
              name={user.displayName}
              id={user.id}
              metadata={Helper.getProfileOrGroupType(this.localizationService, user.loginName || user.userPrincipalName)}
              profileImage={Helper.getProfileOrGroupImage(this.props.environment, this.props.tenant, this.props.token, user.email, user.loginName || user.userPrincipalName)}
              backgroundColor={this.props.hightlightColor}
            />
          </div>
        );
      });
    }
    return (
      <div className={styles.peopleSearch} style={this.props.style}>
        <div
          className={styles.peopleSearchWrapper}
          style={{
            backgroundColor: this.props.backgroundColor != undefined ? this.props.backgroundColor : "transparent",
            border: this.props.backgroundColor != undefined ? "none" : "1px solid #dddddd"
          }}
        >
          {selectedPeople.length > 0 &&
            <span className={styles.selectedPeopleWrapper}>
              {selectedPeople}
            </span>
          }
          {!(this.props.multiSelection == false && this.state.selectedPeople.length >= 1) &&
            <input
              disabled={this.props.disabed}
              type="text" id="peopleSearchField" className={styles.peopleSearchField}
              aria-label={this.props.placeholderText || "Search people"}
              placeholder={this.props.placeholderText}
              value={this.state.searchQuery}
              onChange={async (evt) => {
                let searchQuery = evt.target.value;
                this.setState({ isLoading: true, searchQuery }, async () => {
                  let searchQueryInput = this.state.searchQuery?.trim();
                  if (searchQueryInput) {
                    let userResult = await UserService.getUsers(this.props.environment, this.props.token, `${this.props.tenant}.sharepoint.com`, searchQueryInput, this.props.showGroups);

                    // Trim string before comparing
                    let searchQueryResponse = userResult.searchQuery;
                    if (searchQueryResponse) { searchQueryResponse = searchQueryResponse.trim(); }

                    // Make sure we see the results from the latest search input
                    if ((this.state.searchQuery?.trim() == searchQueryResponse) || (!this.state.searchQuery?.trim() && !searchQueryResponse)) {
                      // Filter result with people already selected
                      // Graph does not support 'not equals' for Azure AD resources so we cannot do it server side
                      if (userResult != undefined && userResult.users != undefined && userResult.users.length > 0 && this.state.selectedPeople != undefined) {

                        this.state.selectedPeople.forEach(user => {
                          userResult.users = userResult.users.filter(u => u.id !== user.id);
                        });
                      }
                      this.setState({ userResult: undefined }, () => {
                        setTimeout(() => {
                          this.setState({ userResult, isLoading: false });
                        }, 0);
                      });
                    }
                  }
                  else {
                    const emptyUserResult: IUserResult = {
                      searchQuery: "",
                      users: []
                    };
                    this.setState({ isLoading: false, userResult: emptyUserResult });
                  }
                });
              }}
            />
          }
          {this.state.isLoading &&
            <div className={styles.spinner}>
              <IAIcon
                title="Spinner"
                size={20}
                color="#333333"
              />
            </div>
          }
        </div>
        {suggestedPeople.length > 0 &&
          <div className={styles.suggestedPeopleContainer}>
            {suggestedPeople}
          </div>
        }
      </div >
    );
  }


}